/**
 * MythicBanner Component
 * Displays the mythic aircraft claim status
 * - Unclaimed: "No one has claimed it... yet."
 * - Claimed: "[Username] - [Date]"
 */

import React from "react";
import { useTranslation } from "react-i18next";

export default function MythicBanner({ mythicClaim }) {
  const { t } = useTranslation();
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1a0a2e 0%, #0d0015 50%, #1a0a2e 100%)",
        border: "1px solid #4a1d6e",
        borderRadius: "8px",
        padding: "16px 24px",
        marginBottom: "16px",
      }}
    >
      {/* Animated shimmer effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(138, 43, 226, 0.1) 50%, transparent 100%)",
          animation: "shimmer 3s infinite",
        }}
      />

      {/* Hexagon icon */}
      <div className="flex items-center gap-4">
        <div
          style={{
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: mythicClaim
              ? "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)"
              : "linear-gradient(135deg, #2d1f3d 0%, #1a0a2e 100%)",
            borderRadius: "8px",
            border: mythicClaim ? "2px solid #d4a5ff" : "2px solid #4a1d6e",
            boxShadow: mythicClaim ? "0 0 20px rgba(138, 43, 226, 0.5)" : "none",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={mythicClaim ? "#ffffff" : "#6b4d8a"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>

        <div className="flex-1">
          <div
            className="flex items-center gap-2"
            style={{
              fontFamily: "monospace",
              fontSize: "14px",
              fontWeight: "bold",
              letterSpacing: "2px",
              color: mythicClaim ? "#d4a5ff" : "#6b4d8a",
              textTransform: "uppercase",
            }}
          >
            {t('leaderboard.mythic')}
          </div>

          <div
            style={{
              fontFamily: "monospace",
              fontSize: "16px",
              color: mythicClaim ? "#e0e0e0" : "#4a4a6a",
              marginTop: "2px",
            }}
          >
            {mythicClaim ? (
              <>
                <span style={{ color: "#d4a5ff", fontWeight: "bold" }}>
                  {mythicClaim.username}
                </span>
                <span style={{ color: "#6b4d8a" }}> - </span>
                <span>{formatDate(mythicClaim.claimed_at)}</span>
              </>
            ) : (
              t('leaderboard.mythicUnclaimed')
            )}
          </div>
        </div>

        {/* Decorative elements */}
        {mythicClaim && (
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#d4a5ff",
              boxShadow: "0 0 10px #d4a5ff, 0 0 20px #9b59b6",
              animation: "pulse 2s infinite",
            }}
          />
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
